'use client';

// TEMPORARY diagnostic instrumentation for the bug:
//   "in mobile viewport, when dragging the mobileChatWrapper to the top, the
//    input field goes crazy and re-renders constantly."
//
// It captures three correlated streams, all filterable in Seq by
//   instanceId = MOBILE_DRAG_RERENDER_INSTANCE_ID
// (or scope = 'persistent-debug-log' AND feature = 'mobile-drag-rerender'):
//
//   1. useResizeStormProbe      — the RAW firing rate of window/visualViewport
//                                 resize + scroll events at the source (the
//                                 suspected driver), independent of React.
//   2. useRenderStormProbe      — a per-COMMIT render trace for every component
//                                 in the mobile-chat subtree, logging EVERY
//                                 render (incl. no-op cascades) with the exact
//                                 keys that changed, so the storm's origin and
//                                 propagation are visible end-to-end.
//   3. drag lifecycle + grab-offset markers — to bound the drag gesture window
//                                 on the timeline and correlate it with 1 & 2.
//
// Remove this whole file + its call sites once the cause is confirmed + fixed.

import { useCallback, useEffect, useId, useLayoutEffect, useRef } from 'react';
import {
  usePersistentDebugLogger,
  type PersistentDebugLogger,
} from './use-persistent-debug-logger';

export const MOBILE_DRAG_RERENDER_INSTANCE_ID = 'mobile-drag-rerender--2026-06-21';
export const MOBILE_DRAG_RERENDER_FEATURE = 'mobile-drag-rerender';

// Stamped once when THIS module evaluates. If it changes between two events the
// module was reloaded (HMR / Fast Refresh) — i.e. a dev remount, not a re-render.
export const MOBILE_DRAG_RERENDER_MODULE_EVAL_ID = Date.now().toString(36);
const MOBILE_DRAG_MOUNT_INSTANCE_SEPARATOR = '#';

export enum MobileDragRerenderEvent {
  Render = 'mdr-render',
  Mount = 'mdr-mount',
  Unmount = 'mdr-unmount',
  ResizeEvent = 'mdr-resize-event',
  DragStart = 'mdr-drag-start',
  DragMove = 'mdr-drag-move',
  DragEnd = 'mdr-drag-end',
  GrabOffsetChange = 'mdr-grab-offset-change',
  IdentityChange = 'mdr-identity-change',
}

export enum MobileDragRerenderResizeSource {
  WindowResize = 'window-resize',
  VisualViewportResize = 'visual-viewport-resize',
  VisualViewportScroll = 'visual-viewport-scroll',
  OrientationChange = 'orientation-change',
}

function nowMs(): number {
  return typeof performance !== 'undefined' ? performance.now() : 0;
}

export function useMobileDragRerenderLogger(
  component: string,
  metadata?: Record<string, unknown>,
): PersistentDebugLogger {
  return usePersistentDebugLogger({
    component,
    instanceId: MOBILE_DRAG_RERENDER_INSTANCE_ID,
    metadata: {
      feature: MOBILE_DRAG_RERENDER_FEATURE,
      moduleEvalId: MOBILE_DRAG_RERENDER_MODULE_EVAL_ID,
      ...(metadata ?? {}),
    },
  });
}

// Stable per-mount id. A remount (new component instance) gets a new id, so two
// consecutive render events with different mountInstanceId == a remount, not a
// re-render of the same instance.
export function useMobileDragMountInstanceId(): string {
  const reactInstanceId = useId();
  return `${MOBILE_DRAG_RERENDER_MODULE_EVAL_ID}${MOBILE_DRAG_MOUNT_INSTANCE_SEPARATOR}${reactInstanceId}`;
}

interface RenderStormProbeInput {
  logger: PersistentDebugLogger;
  component: string;
  snapshot: Record<string, unknown>;
}

// Logs on EVERY commit — unlike the existing useRenderChangeProbe, which skips
// no-op renders. The bug is "renders constantly", so a render where NONE of the
// tracked values changed (changedKeys empty) is itself the evidence: the storm
// originates ABOVE this component and is cascading down. Each event carries the
// render frequency (renderCount + sinceLastMs), exactly which tracked keys
// changed and their before/after, and the mount instance id (re-render vs
// remount). Snapshot values must be primitives or stable identity refs so the
// per-key Object.is comparison is meaningful.
export function useRenderStormProbe({
  logger,
  component,
  snapshot,
}: RenderStormProbeInput): void {
  const mountInstanceId = useMobileDragMountInstanceId();
  const renderCountRef = useRef(0);
  const prevSnapshotRef = useRef<Record<string, unknown> | null>(null);
  const lastCommitMsRef = useRef<number | null>(null);

  useLayoutEffect(() => {
    renderCountRef.current += 1;
    const commitMs = nowMs();
    const sinceLastMs =
      lastCommitMsRef.current === null ? null : commitMs - lastCommitMsRef.current;
    lastCommitMsRef.current = commitMs;

    const previous = prevSnapshotRef.current;
    prevSnapshotRef.current = snapshot;

    const changes: Record<string, { from: unknown; to: unknown }> = {};
    if (previous !== null) {
      Object.keys(snapshot).forEach((key) => {
        if (!Object.is(previous[key], snapshot[key])) {
          changes[key] = { from: previous[key] ?? null, to: snapshot[key] ?? null };
        }
      });
    }
    const changedKeys = Object.keys(changes);

    logger.logStateTransition({
      name: MobileDragRerenderEvent.Render,
      from: previous,
      to: snapshot,
      payload: {
        component,
        mountInstanceId,
        renderCount: renderCountRef.current,
        sinceLastMs,
        changedKeys,
        changes,
        isFirstRender: previous === null,
        // A re-render where nothing we track changed => driven from above.
        isNoOpCascade: previous !== null && changedKeys.length === 0,
        tMs: commitMs,
      },
    });
  });
}

// Logs a mount marker on mount and an unmount marker on unmount, tagged with
// the per-mount instance id. Two overlapping mount/unmount lifetimes (a mount
// with no intervening unmount of the previous instance) == a real concurrent
// double-mount, the smoking gun for "two stacked trees fighting". `label` lets
// one component distinguish multiple call sites.
export function useMountUnmountProbe(input: {
  logger: PersistentDebugLogger;
  label: string;
}): void {
  const { logger, label } = input;
  const mountInstanceId = useMobileDragMountInstanceId();
  useLayoutEffect(() => {
    logger.logMarker({
      name: MobileDragRerenderEvent.Mount,
      payload: { label, mountInstanceId, tMs: nowMs() },
    });
    return () => {
      logger.logMarker({
        name: MobileDragRerenderEvent.Unmount,
        payload: { label, mountInstanceId, tMs: nowMs() },
      });
    };
  }, [logger, label, mountInstanceId]);
}

// Tracks the REFERENCE identity of each provided value across commits and emits
// which keys changed identity per render. Aimed at finding the re-render DRIVER:
// pass the raw context/hook return values a component subscribes to — the key
// whose identity flips at the storm cadence is the provider that re-rendered and
// pushed a new value. Unlike useRenderStormProbe it logs only a per-key version
// integer (bumped on each identity change), never serializing the objects, so it
// is safe for huge/circular context values.
export function useIdentityStormProbe({
  logger,
  component,
  identities,
}: {
  logger: PersistentDebugLogger;
  component: string;
  identities: Record<string, unknown>;
}): void {
  const mountInstanceId = useMobileDragMountInstanceId();
  const renderCountRef = useRef(0);
  const prevRef = useRef<Record<string, unknown> | null>(null);
  const versionsRef = useRef<Record<string, number>>({});
  const lastCommitMsRef = useRef<number | null>(null);

  useLayoutEffect(() => {
    renderCountRef.current += 1;
    const commitMs = nowMs();
    const sinceLastMs =
      lastCommitMsRef.current === null ? null : commitMs - lastCommitMsRef.current;
    lastCommitMsRef.current = commitMs;

    const previous = prevRef.current;
    prevRef.current = identities;
    const versions = versionsRef.current;
    const changedKeys: string[] = [];

    Object.keys(identities).forEach((key) => {
      if (previous === null) {
        versions[key] = 0;
        return;
      }
      if (!Object.is(previous[key], identities[key])) {
        versions[key] = (versions[key] ?? 0) + 1;
        changedKeys.push(key);
      }
    });

    logger.logStateTransition({
      name: MobileDragRerenderEvent.IdentityChange,
      from: null,
      to: null,
      payload: {
        component,
        mountInstanceId,
        renderCount: renderCountRef.current,
        sinceLastMs,
        changedKeys,
        versions: { ...versions },
        isFirstRender: previous === null,
        tMs: commitMs,
      },
    });
  });
}

interface ResizeStormProbeInput {
  logger: PersistentDebugLogger;
  active: boolean;
}

interface ResizeStormDimensions {
  innerWidth: number;
  innerHeight: number;
  vvWidth: number | null;
  vvHeight: number | null;
  width: number;
  height: number;
  vvOffsetTop: number | null;
  vvScale: number | null;
}

interface ResizeStormPreviousSample {
  lastMs: number | null;
  lastWidth: number;
  lastHeight: number;
}

interface ResizeStormPayload extends Record<string, unknown> {
  source: MobileDragRerenderResizeSource;
  eventCount: number;
  sinceLastMs: number | null;
  widthDelta: number;
  heightDelta: number;
  innerWidth: number;
  innerHeight: number;
  vvWidth: number | null;
  vvHeight: number | null;
  vvOffsetTop: number | null;
  vvScale: number | null;
  tMs: number;
}

function getResizeStormDimensions(
  visualViewport: VisualViewport | null,
): ResizeStormDimensions {
  const innerWidth = window.innerWidth;
  const innerHeight = window.innerHeight;

  if (!visualViewport) {
    return {
      innerWidth,
      innerHeight,
      vvWidth: null,
      vvHeight: null,
      width: innerWidth,
      height: innerHeight,
      vvOffsetTop: null,
      vvScale: null,
    };
  }

  return {
    innerWidth,
    innerHeight,
    vvWidth: visualViewport.width,
    vvHeight: visualViewport.height,
    width: visualViewport.width,
    height: visualViewport.height,
    vvOffsetTop: visualViewport.offsetTop,
    vvScale: visualViewport.scale,
  };
}

function buildResizeStormPayload(input: {
  source: MobileDragRerenderResizeSource;
  eventCount: number;
  previousSample: ResizeStormPreviousSample;
  dimensions: ResizeStormDimensions;
  commitMs: number;
}): ResizeStormPayload {
  return {
    source: input.source,
    eventCount: input.eventCount,
    sinceLastMs:
      input.previousSample.lastMs == null
        ? null
        : input.commitMs - input.previousSample.lastMs,
    widthDelta: input.dimensions.width - input.previousSample.lastWidth,
    heightDelta: input.dimensions.height - input.previousSample.lastHeight,
    innerWidth: input.dimensions.innerWidth,
    innerHeight: input.dimensions.innerHeight,
    vvWidth: input.dimensions.vvWidth,
    vvHeight: input.dimensions.vvHeight,
    vvOffsetTop: input.dimensions.vvOffsetTop,
    vvScale: input.dimensions.vvScale,
    tMs: input.commitMs,
  };
}

// One global listener set (mount once, e.g. from the mobile chat) capturing the
// raw firing rate of window resize / visualViewport resize + scroll /
// orientationchange — the suspected driver behind useViewportMetrics rebuilding
// its metrics object. Logs every event with current dimensions, deltas vs the
// previous event, and inter-event ms, so a height oscillation storm during a
// vertical drag is visible at the source — even when the width never changes
// (the case where consumers reading only `vw` still re-render).
export function useResizeStormProbe({ logger, active }: ResizeStormProbeInput): void {
  useEffect(() => {
    if (!active || typeof window === 'undefined') {
      return undefined;
    }

    const visualViewport = window.visualViewport;
    let lastMs: number | null = null;
    let lastWidth = 0;
    let lastHeight = 0;
    let eventCount = 0;

    const record = (source: MobileDragRerenderResizeSource): void => {
      const commitMs = nowMs();
      eventCount += 1;
      const dimensions = getResizeStormDimensions(visualViewport ?? null);

      logger.logMarker({
        name: MobileDragRerenderEvent.ResizeEvent,
        payload: buildResizeStormPayload({
          source,
          eventCount,
          previousSample: { lastMs, lastWidth, lastHeight },
          dimensions,
          commitMs,
        }),
      });

      lastMs = commitMs;
      lastWidth = dimensions.width;
      lastHeight = dimensions.height;
    };

    const onWindowResize = (): void =>
      record(MobileDragRerenderResizeSource.WindowResize);
    const onOrientationChange = (): void =>
      record(MobileDragRerenderResizeSource.OrientationChange);
    const onVisualViewportResize = (): void =>
      record(MobileDragRerenderResizeSource.VisualViewportResize);
    const onVisualViewportScroll = (): void =>
      record(MobileDragRerenderResizeSource.VisualViewportScroll);

    window.addEventListener('resize', onWindowResize, { passive: true });
    window.addEventListener('orientationchange', onOrientationChange);
    visualViewport?.addEventListener('resize', onVisualViewportResize);
    visualViewport?.addEventListener('scroll', onVisualViewportScroll);

    return () => {
      window.removeEventListener('resize', onWindowResize);
      window.removeEventListener('orientationchange', onOrientationChange);
      visualViewport?.removeEventListener('resize', onVisualViewportResize);
      visualViewport?.removeEventListener('scroll', onVisualViewportScroll);
    };
  }, [active, logger]);
}

// Returns a throttled marker emitter for high-frequency gesture callbacks
// (onDrag, MotionValue change subscriptions) so the timeline stays readable
// while still proving the gesture was active during a render/resize storm.
export function useThrottledMarkLogger(input: {
  logger: PersistentDebugLogger;
  minIntervalMs: number;
}): (name: MobileDragRerenderEvent, payload: Record<string, unknown>) => void {
  const { logger, minIntervalMs } = input;
  const lastMsRef = useRef(0);

  return useCallback(
    (name: MobileDragRerenderEvent, payload: Record<string, unknown>): void => {
      const commitMs = nowMs();
      if (commitMs - lastMsRef.current < minIntervalMs) {
        return;
      }
      lastMsRef.current = commitMs;
      logger.logMarker({ name, payload: { ...payload, tMs: commitMs } });
    },
    [logger, minIntervalMs],
  );
}
